import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export const OutletIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 21h18" />
    <path d="M5 21V8l7-4 7 4v13" />
    <path d="M9 21v-6h6v6" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0z" />
    <circle cx="12" cy="10" r="2" />
  </svg>
);

const CheckIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const isPrimaryOutlet = (outlet) => [true, 1, "1", "true"].includes(outlet?.is_default);

const getOutletAccent = (outlet) => {
  const color = outlet?.effective_primary_color || outlet?.primary_color;
  return /^#[0-9a-f]{6}$/i.test(String(color || "")) ? color : "#0B2D32";
};

const OutletPickerModal = ({
  open,
  outlets = [],
  activeOutlet,
  selectedId,
  switching = false,
  onSelect,
  onClose,
  onConfirm,
}) => {
  const dialogRef = useRef(null);
  const selectedOutlet = outlets.find((outlet) => Number(outlet.id) === Number(selectedId));
  const hasOutletChanged = selectedOutlet && Number(selectedOutlet.id) !== Number(activeOutlet?.id);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !switching) onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, switching, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="outlet-picker-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <section
        ref={dialogRef}
        className="outlet-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="outlet-picker-title"
        tabIndex="-1">
        <header className="outlet-picker-header">
          <div className="outlet-picker-heading">
            <span className="outlet-picker-heading-icon"><OutletIcon size={21} /></span>
            <div>
              <small>PILIH LOKASI OPERASIONAL</small>
              <h2 id="outlet-picker-title">Beralih outlet</h2>
              <p>Transaksi, stok, kas, dan laporan akan mengikuti outlet yang dipilih.</p>
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Tutup pilihan outlet" disabled={switching} onClick={onClose} />
        </header>

        <div className="outlet-picker-body">
          <div className="outlet-picker-current">
            <span className="outlet-live-dot" aria-hidden="true" />
            <span>Saat ini Anda bekerja di</span>
            <strong>{activeOutlet?.name}</strong>
          </div>

          <div className="outlet-picker-grid" role="radiogroup" aria-label="Daftar outlet aktif">
            {outlets.map((outlet) => {
              const isCurrent = Number(outlet.id) === Number(activeOutlet?.id);
              const isSelected = Number(outlet.id) === Number(selectedId);
              const isPrimary = isPrimaryOutlet(outlet);

              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={`outlet-picker-option ${isSelected ? "is-selected" : ""} ${isCurrent ? "is-current" : ""}`.trim()}
                  key={outlet.id}
                  disabled={switching}
                  onClick={() => onSelect(outlet)}>
                  <span
                    className="outlet-picker-option-icon"
                    style={{ "--outlet-accent": getOutletAccent(outlet) }}>
                    <OutletIcon size={20} />
                  </span>

                  <span className="outlet-picker-option-copy">
                    <span className="outlet-picker-option-topline">
                      <span className="outlet-picker-code">{outlet.code || "OUTLET"}</span>
                      {isPrimary ? <span className="outlet-picker-primary-badge">Utama</span> : null}
                    </span>
                    <strong>{outlet.name}</strong>
                    <span className="outlet-picker-address">
                      <MapPinIcon />
                      <span>{outlet.address || "Alamat outlet belum diisi"}</span>
                    </span>
                  </span>

                  <span className={`outlet-picker-radio ${isSelected ? "is-checked" : ""}`} aria-hidden="true">
                    {isSelected ? <CheckIcon /> : null}
                  </span>

                  <span className="outlet-picker-option-state">
                    {isCurrent ? "Sedang digunakan" : isSelected ? "Siap dipilih" : "Pilih outlet"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <footer className="outlet-picker-footer">
          <button type="button" className="btn btn-link link-secondary" disabled={switching} onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!hasOutletChanged || switching}
            onClick={onConfirm}>
            {switching ? "Mengalihkan outlet..." : hasOutletChanged ? `Gunakan ${selectedOutlet.name}` : "Outlet sedang digunakan"}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
};

export default OutletPickerModal;
